import React, { useState, useCallback } from 'react';
import PushNotificationModal, { NotificationData } from './PushNotificationModal';

interface NotificationManagerProps {
  children: React.ReactNode;
}

// Global notification manager instance
let globalNotificationManager: {
  showNotification: (notification: Omit<NotificationData, 'id' | 'timestamp'>) => void;
} | null = null;

const NotificationManager: React.FC<NotificationManagerProps> = ({ children }) => {
  const [currentNotification, setCurrentNotification] = useState<NotificationData | null>(null);
  const [notificationQueue, setNotificationQueue] = useState<NotificationData[]>([]);

  const showNotification = useCallback((notification: Omit<NotificationData, 'id' | 'timestamp'>) => {
    const newNotification: NotificationData = {
      ...notification,
      id: `notification_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
      timestamp: Date.now()
    };

    if (currentNotification) {
      // If there's already a notification showing, queue this one
      setNotificationQueue(prev => [...prev, newNotification]);
    } else {
      // Show immediately
      setCurrentNotification(newNotification);
    }
  }, [currentNotification]);

  const handleNotificationClose = useCallback(() => {
    setCurrentNotification(null);
    
    // Show next notification in queue if any
    setNotificationQueue(prev => {
      if (prev.length > 0) {
        const [next, ...rest] = prev;
        setCurrentNotification(next);
        return rest;
      }
      return prev;
    });
  }, []);

  // Set global reference
  React.useEffect(() => {
    globalNotificationManager = { showNotification };
    
    return () => {
      globalNotificationManager = null;
    };
  }, [showNotification]);

  return (
    <>
      {children}
      <PushNotificationModal
        notification={currentNotification}
        onClose={handleNotificationClose}
        autoCloseDelay={6000} // 6 seconds
      />
    </>
  );
};

// Global function to show notifications from anywhere in the app
export const showPushNotification = (notification: Omit<NotificationData, 'id' | 'timestamp'>) => {
  if (globalNotificationManager) {
    globalNotificationManager.showNotification(notification);
  } else {
    console.warn('NotificationManager not initialized. Make sure to wrap your app with NotificationManager.');
  }
};

// Helper functions for common notification types
export const showJobStatusNotification = (jobTitle: string, newStatus: string, dispatchId?: string) => {
  showPushNotification({
    title: 'Job Status Updated',
    body: `${jobTitle} is now ${newStatus}`,
    data: {
      type: 'job_status',
      dispatch_id: dispatchId,
      url: dispatchId ? `/tenant/dispatches/${dispatchId}` : undefined
    }
  });
};

export const showDispatchUpdateNotification = (message: string, dispatchId?: string) => {
  showPushNotification({
    title: 'Dispatch Update',
    body: message,
    data: {
      type: 'dispatch_update',
      dispatch_id: dispatchId,
      url: dispatchId ? `/tenant/dispatches/${dispatchId}` : undefined
    }
  });
};

export const showGeneralNotification = (title: string, message: string, url?: string) => {
  showPushNotification({
    title,
    body: message,
    data: {
      type: 'general',
      url
    }
  });
};

export default NotificationManager;
